/**
 * Auth-flow search params — the SINGLE chokepoint for cross-links within the
 * auth pages (login ↔ register ↔ forgot-password).
 *
 * Multi-account: the switcher opens `/login?mode=add` to ADD a second account
 * while the current one stays signed in, and auth pages carry a `redirect` back
 * to the originating tab's `/u/<n>`. Both MUST survive when the user moves
 * BETWEEN auth pages (e.g. "Register" / "Forgot your password?" from the add
 * login). If a cross-link drops `mode=add`, the `_auth` guard sees the global
 * session and bounces you into the existing account instead of letting you
 * register/recover a new one.
 *
 * RULE: every `<Link>` between auth pages MUST set `search={authFlowSearch(search())}`
 * — never hand-build the search object. This way the intent can't be forgotten
 * in one place and silently break the add-account / reset flow.
 */
interface AuthFlowSearch {
  /** Path to return to after auth (carries the originating tab's `/u/<n>`). */
  redirect?: string;
  /** `add` = adding another account alongside the current session (Gmail-style). */
  mode?: "add";
}

/** Preserves the auth-flow intent (`mode` + `redirect`) across auth cross-links. */
export function authFlowSearch(current: AuthFlowSearch): AuthFlowSearch {
  return {
    redirect: current.redirect,
    mode: current.mode === "add" ? "add" : undefined,
  };
}
